import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that persists the selected installation method as a cart attribute.
 *
 * @typedef {Object} CartInstallationMethodRefs
 * @property {HTMLElement} installationMethodError - The validation error element.
 */

/**
 * @extends {Component<CartInstallationMethodRefs>}
 */
class CartInstallationMethod extends Component {
  requiredRefs = ['installationMethodError'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('invalid', this.#handleInvalid, true);
  }

  /**
   * Handles updates to the cart installation method.
   * @param {Event} event - The change event from a radio input.
   */
  updateInstallationMethod = async (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;

    const installationMethod = event.target.value;
    if (!installationMethod) return;

    this.#syncInputs(installationMethod);

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          attributes: {
            installation_method: installationMethod,
          },
        }),
      });

      await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('installation-method-update:user-action', event);
    }
  };

  /**
   * Keeps duplicate controls on the page visually aligned after a change.
   * @param {string} installationMethod
   */
  #syncInputs(installationMethod) {
    const inputs = document.querySelectorAll('input[name="attributes[installation_method]"]');

    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      input.checked = input.value === installationMethod;
      input.setCustomValidity('');
    }

    for (const instance of document.querySelectorAll('cart-installation-method')) {
      if (!(instance instanceof CartInstallationMethod)) continue;

      instance.setAttribute('data-selected', '');
      instance.refs.installationMethodError.classList.add('hidden');
    }
  }

  /**
   * Shows a validation error when the browser blocks checkout because no installation method is selected.
   * @param {Event} event - The invalid event from a radio input.
   */
  #handleInvalid = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;

    event.preventDefault();

    const message = this.refs.installationMethodError.textContent?.trim() ?? '';
    event.target.setCustomValidity(message);
    this.refs.installationMethodError.classList.remove('hidden');
  };
}

if (!customElements.get('cart-installation-method')) {
  customElements.define('cart-installation-method', CartInstallationMethod);
}